import { ChecklistTemplate } from './types';

/**
 * Plantilla de revisión para Grupo de Presión contra incendios.
 *
 * Referencias normativas:
 *   - RD 513/2017 (RIPCI), Anexo II — operaciones de mantenimiento.
 *   - UNE-EN 12845 (sistemas fijos de extinción · rociadores automáticos – grupos de bombeo).
 *   - UNE 23500 (sistemas de abastecimiento de agua contra incendios).
 *
 * Periodicidad: T = Trimestral · S = Semestral · A = Anual.
 */
export const GRUPO_PRESION_TEMPLATE: ChecklistTemplate = {
  tipo_sistema: 'pci_grupo_presion',
  titulo: 'Grupo de Presión contra incendios',
  protocolos: [
    {
      id: 'inventario',
      titulo: '1. Inventario y configuración general',
      cabecera: [
        { key: 'norma_diseno', label: 'Norma de diseño (UNE-EN 12845 / UNE 23500)' },
        { key: 'categoria_suministro', label: 'Categoría suministro (sencillo / superior)' },
        { key: 'configuracion', label: 'Configuración (J+E / J+D / J+E+D / E+E / E+D)' },
        { key: 'caudal_nominal_lpm', label: 'Caudal nominal (l/min)', tipo: 'number' },
        { key: 'presion_nominal_bar', label: 'Presión nominal (bar)', tipo: 'number' },
        { key: 'volumen_aspiracion_m3', label: 'Volumen depósito aspiración (m³)', tipo: 'number' },
      ],
      preguntas: [
        { id: 'IN.01', texto: '¿La configuración del grupo (jockey + principal/reserva) coincide con el proyecto y la categoría de suministro?', periodicidad: ['A'] },
        { id: 'IN.02', texto: '¿Los caudales y presiones nominales corresponden a los riesgos protegidos (BIE, rociadores, hidrantes)?', periodicidad: ['A'] },
        { id: 'IN.03', texto: '¿Existe esquema hidráulico, eléctrico y curva característica visibles en la sala de bombas?', periodicidad: ['A'] },
        { id: 'IN.04', texto: '¿Está disponible y actualizada la documentación del fabricante y el libro de mantenimiento?', periodicidad: ['A'] },
      ],
    },
    {
      id: 'sala_bombas',
      titulo: '2. Sala de bombas',
      cabecera: [
        { key: 'temperatura_sala', label: 'Temperatura sala (°C)', tipo: 'number' },
        { key: 'iluminancia_lux', label: 'Iluminancia (lux)', tipo: 'number' },
      ],
      preguntas: [
        { id: 'SB.01', texto: '¿La sala está limpia, libre de obstáculos y de uso exclusivo para PCI?', periodicidad: ['T','S','A'] },
        { id: 'SB.02', texto: '¿La temperatura se mantiene > 4 °C (sin riesgo de heladas) y < 40 °C?', periodicidad: ['T','S','A'] },
        { id: 'SB.03', texto: '¿La ventilación natural/forzada es suficiente para evacuar el calor del motor diésel?', periodicidad: ['S','A'] },
        { id: 'SB.04', texto: '¿Existe iluminación normal y de emergencia operativa?', periodicidad: ['S','A'] },
        { id: 'SB.05', texto: '¿La sala dispone de sumidero y desagüe operativos para evacuar drenajes y pruebas?', periodicidad: ['S','A'] },
        { id: 'SB.06', texto: '¿El acceso está señalizado y la puerta es de uso exclusivo del personal autorizado?', periodicidad: ['T','S','A'] },
        { id: 'SB.07', texto: '¿Existe extintor portátil adecuado (CO₂ o polvo ABC) próximo a la sala?', periodicidad: ['T','S','A'] },
      ],
    },
    {
      id: 'aspiracion',
      titulo: '3. Aspiración y depósito',
      cabecera: [
        { key: 'tipo_aspiracion', label: 'Tipo (en carga / aspiración negativa)' },
        { key: 'nivel_actual_m3', label: 'Nivel actual depósito (m³)', tipo: 'number' },
        { key: 'autonomia_min', label: 'Autonomía a caudal nominal (min)', tipo: 'number' },
      ],
      preguntas: [
        { id: 'AS.01', texto: '¿El depósito está al nivel mínimo de reserva (lleno o por encima del nivel de rearme)?', periodicidad: ['T','S','A'] },
        { id: 'AS.02', texto: '¿El llenado automático funciona y el caudal de reposición cumple con UNE-EN 12845 (≤ 36 h)?', periodicidad: ['S','A'] },
        { id: 'AS.03', texto: '¿Las válvulas de aspiración están totalmente abiertas, precintadas/candadas en posición operativa?', periodicidad: ['T','S','A'] },
        { id: 'AS.04', texto: '¿Los filtros y crepinas de aspiración están limpios y sin obstrucciones?', periodicidad: ['A'] },
        { id: 'AS.05', texto: '¿La aspiración está libre de fugas, golpes y corrosión visible?', periodicidad: ['S','A'] },
        { id: 'AS.06', texto: '¿Existen y funcionan los detectores de nivel mínimo y bajo en el depósito?', periodicidad: ['S','A'] },
        { id: 'AS.07', texto: '¿Se ha verificado la limpieza interior del depósito (inspección decenal)?', periodicidad: ['A'] },
      ],
    },
    {
      id: 'bomba_jockey',
      titulo: '4. Bomba jockey (mantenimiento de presión)',
      cabecera: [
        { key: 'marca', label: 'Marca' },
        { key: 'modelo', label: 'Modelo' },
        { key: 'numero_serie', label: 'Nº Serie' },
        { key: 'presion_arranque', label: 'Presión arranque (bar)', tipo: 'number' },
        { key: 'presion_parada', label: 'Presión parada (bar)', tipo: 'number' },
        { key: 'arranques_inicio', label: 'Contador arranques inicio', tipo: 'number' },
        { key: 'arranques_fin', label: 'Contador arranques fin', tipo: 'number' },
      ],
      preguntas: [
        { id: 'BJ.01', texto: '¿La bomba jockey arranca y para automáticamente dentro del rango de presión configurado?', periodicidad: ['T','S','A'], unidades: true },
        { id: 'BJ.02', texto: '¿El número de arranques en el periodo es coherente (sin ciclos excesivos por fugas)?', periodicidad: ['T','S','A'], unidades: true },
        { id: 'BJ.03', texto: '¿El acoplamiento, prensaestopas/cierre mecánico y cuerpo de bomba están libres de fugas?', periodicidad: ['S','A'], unidades: true },
        { id: 'BJ.04', texto: '¿La presión de parada es inferior a la de arranque de la bomba principal (escalonamiento correcto)?', periodicidad: ['A'], unidades: true },
        { id: 'BJ.05', texto: '¿El motor eléctrico no presenta sobrecalentamiento, ruido anómalo ni vibraciones?', periodicidad: ['S','A'], unidades: true },
      ],
    },
    {
      id: 'bomba_electrica',
      titulo: '5. Bomba principal eléctrica',
      cabecera: [
        { key: 'marca', label: 'Marca' },
        { key: 'modelo', label: 'Modelo' },
        { key: 'numero_serie', label: 'Nº Serie' },
        { key: 'caudal_lpm', label: 'Caudal (l/min)', tipo: 'number' },
        { key: 'presion_bar', label: 'Presión (bar)', tipo: 'number' },
        { key: 'potencia_kw', label: 'Potencia motor (kW)', tipo: 'number' },
        { key: 'horas_inicio', label: 'Contador horas inicio', tipo: 'number' },
        { key: 'horas_fin', label: 'Contador horas fin', tipo: 'number' },
      ],
      preguntas: [
        { id: 'BE.01', texto: '¿La bomba arranca manualmente desde el cuadro y mediante el presostato de demanda?', periodicidad: ['T','S','A'], unidades: true },
        { id: 'BE.02', texto: '¿Se ha realizado prueba de arranque semanal automática durante al menos 10 minutos?', periodicidad: ['T','S','A'], unidades: true },
        { id: 'BE.03', texto: '¿La presión y el caudal en prueba se ajustan a la curva característica del fabricante?', periodicidad: ['A'], unidades: true },
        { id: 'BE.04', texto: '¿La parada de la bomba es siempre manual (según UNE-EN 12845)?', periodicidad: ['A'], unidades: true },
        { id: 'BE.05', texto: '¿El cuerpo de bomba, juntas y acoplamiento están libres de fugas, vibraciones y ruidos?', periodicidad: ['S','A'], unidades: true },
        { id: 'BE.06', texto: '¿La temperatura del motor y de los cojinetes se mantiene dentro de los valores del fabricante?', periodicidad: ['S','A'], unidades: true },
        { id: 'BE.07', texto: '¿La alineación del acoplamiento bomba-motor es correcta (sin desgaste anómalo)?', periodicidad: ['A'], unidades: true },
      ],
    },
    {
      id: 'bomba_diesel',
      titulo: '6. Bomba principal diésel',
      cabecera: [
        { key: 'marca', label: 'Marca' },
        { key: 'modelo', label: 'Modelo' },
        { key: 'numero_serie', label: 'Nº Serie' },
        { key: 'caudal_lpm', label: 'Caudal (l/min)', tipo: 'number' },
        { key: 'presion_bar', label: 'Presión (bar)', tipo: 'number' },
        { key: 'horas_inicio', label: 'Contador horas inicio', tipo: 'number' },
        { key: 'horas_fin', label: 'Contador horas fin', tipo: 'number' },
        { key: 'capacidad_combustible_l', label: 'Capacidad depósito gasoil (l)', tipo: 'number' },
        { key: 'autonomia_h', label: 'Autonomía estimada (h)', tipo: 'number' },
      ],
      preguntas: [
        { id: 'BD.01', texto: '¿La bomba arranca manualmente y automáticamente con cada batería por separado y alcanza régimen en < 15 s?', periodicidad: ['T','S','A'], unidades: true },
        { id: 'BD.02', texto: '¿Se ha realizado prueba semanal de funcionamiento durante al menos 30 minutos?', periodicidad: ['T','S','A'], unidades: true },
        { id: 'BD.03', texto: '¿La presión y el caudal en prueba se ajustan a la curva del fabricante?', periodicidad: ['A'], unidades: true },
        { id: 'BD.04', texto: '¿El depósito de gasoil está ≥ 2/3 de su capacidad y libre de agua/sedimentos?', periodicidad: ['T','S','A'], unidades: true },
        { id: 'BD.05', texto: '¿Niveles de aceite, refrigerante y electrolito de baterías dentro de los valores correctos?', periodicidad: ['T','S','A'], unidades: true },
        { id: 'BD.06', texto: '¿El precalentamiento de motor (resistencia / agua) funciona y mantiene la temperatura mínima?', periodicidad: ['T','S','A'], unidades: true },
        { id: 'BD.07', texto: '¿La refrigeración (intercambiador / radiador) y los tubos de escape están libres de fugas y obstrucciones?', periodicidad: ['S','A'], unidades: true },
        { id: 'BD.08', texto: '¿El sistema de escape evacua al exterior con aislamiento térmico íntegro?', periodicidad: ['A'], unidades: true },
        { id: 'BD.09', texto: '¿Se han sustituido filtros (aceite, gasoil, aire) y aceite según plan del fabricante?', periodicidad: ['A'], unidades: true },
      ],
    },
    {
      id: 'baterias',
      titulo: '7. Baterías de arranque (bomba diésel)',
      cabecera: [
        { key: 'tipo_bateria', label: 'Tipo (plomo-ácido / AGM)' },
        { key: 'tension_v', label: 'Tensión nominal (V)', tipo: 'number' },
        { key: 'capacidad_ah', label: 'Capacidad (Ah)', tipo: 'number' },
        { key: 'fecha_instalacion', label: 'Fecha instalación', tipo: 'date' },
      ],
      preguntas: [
        { id: 'BA.01', texto: '¿Existen dos baterías independientes con conmutación automática?', periodicidad: ['T','S','A'] },
        { id: 'BA.02', texto: '¿La tensión en reposo y bajo arranque está dentro de los valores del fabricante?', periodicidad: ['T','S','A'] },
        { id: 'BA.03', texto: '¿Los bornes están limpios, engrasados y sin sulfatación?', periodicidad: ['S','A'] },
        { id: 'BA.04', texto: '¿El cargador de baterías mantiene la tensión de flotación correcta y dispone de alarma de fallo?', periodicidad: ['T','S','A'] },
        { id: 'BA.05', texto: '¿Las baterías tienen una antigüedad inferior a la recomendada por el fabricante (típ. ≤ 5 años)?', periodicidad: ['A'] },
      ],
    },
    {
      id: 'cuadro',
      titulo: '8. Cuadro eléctrico y control',
      preguntas: [
        { id: 'CE.01', texto: '¿El cuadro está alimentado de forma exclusiva y la línea es independiente del cuadro general?', periodicidad: ['A'] },
        { id: 'CE.02', texto: '¿Los pilotos de tensión, marcha, fallo y reserva están operativos?', periodicidad: ['T','S','A'] },
        { id: 'CE.03', texto: '¿Las alarmas (fallo de red, fallo de arranque, nivel mínimo, fallo de cargador) se transmiten a punto vigilado 24 h?', periodicidad: ['S','A'] },
        { id: 'CE.04', texto: '¿El selector está en posición AUTOMÁTICO y precintado?', periodicidad: ['T','S','A'] },
        { id: 'CE.05', texto: '¿Los presostatos de arranque están operativos, calibrados y dobles en bombas principales?', periodicidad: ['S','A'] },
        { id: 'CE.06', texto: '¿La maniobra de prueba (caudalímetro / circuito de prueba) funciona y devuelve el agua al depósito?', periodicidad: ['S','A'] },
      ],
    },
    {
      id: 'impulsion',
      titulo: '9. Colector de impulsión y accesorios',
      preguntas: [
        { id: 'CI.01', texto: '¿El colector de impulsión está libre de fugas, corrosión y vibraciones anómalas?', periodicidad: ['S','A'] },
        { id: 'CI.02', texto: '¿Las válvulas de retención y de seccionamiento operan correctamente y están precintadas en posición operativa?', periodicidad: ['T','S','A'] },
        { id: 'CI.03', texto: '¿Los manómetros (aspiración e impulsión) son legibles y están dentro de calibración?', periodicidad: ['T','S','A'] },
        { id: 'CI.04', texto: '¿El vaso de expansión / acumulador mantiene la presión de hinchado correcta (sin fugas)?', periodicidad: ['A'] },
        { id: 'CI.05', texto: '¿La válvula de seguridad / by-pass de recirculación funciona y descarga al depósito?', periodicidad: ['A'] },
        { id: 'CI.06', texto: '¿El caudalímetro de prueba está operativo y calibrado?', periodicidad: ['A'] },
      ],
    },
    {
      id: 'pruebas',
      titulo: '10. Pruebas funcionales y de prestaciones',
      preguntas: [
        { id: 'PR.01', texto: '¿Se ha realizado prueba a caudal nominal verificando presión y caudal según curva?', periodicidad: ['A'] },
        { id: 'PR.02', texto: '¿Se ha realizado prueba a caudal máximo (150 % nominal) con presión ≥ 65 % de la nominal?', periodicidad: ['A'] },
        { id: 'PR.03', texto: '¿Se ha simulado fallo de red y verificada la conmutación a grupo diésel (si aplica)?', periodicidad: ['A'] },
        { id: 'PR.04', texto: '¿Se ha verificado el tiempo de arranque diésel (< 15 s) en ambas baterías?', periodicidad: ['A'] },
        { id: 'PR.05', texto: '¿Las alarmas y señales han llegado correctamente al puesto vigilado o cra?', periodicidad: ['T','S','A'] },
      ],
    },
    {
      id: 'documentacion',
      titulo: '11. Documentación y registros',
      preguntas: [
        { id: 'DC.01', texto: '¿Existe acta firmada de la última revisión por empresa mantenedora habilitada?', periodicidad: ['T','S','A'] },
        { id: 'DC.02', texto: '¿El libro de mantenimiento está actualizado con todas las operaciones, pruebas y consumos?', periodicidad: ['T','S','A'] },
        { id: 'DC.03', texto: '¿Se conservan los certificados de prueba hidráulica y de prestaciones (curva real)?', periodicidad: ['A'] },
        { id: 'DC.04', texto: '¿Se ha entregado al titular el informe de anomalías y plan de acciones correctoras?', periodicidad: ['A'] },
        { id: 'DC.05', texto: '¿Se conservan los partes de prueba semanal firmados por el responsable de la instalación?', periodicidad: ['T','S','A'] },
      ],
    },
    {
      id: 'prueba_prestaciones',
      titulo: '12. Prueba de prestaciones (curva caudal/presión)',
      preguntas: [],
      tipo_render: 'curva_caudal',
      periodicidad: ['A'],
    },
  ],
};
